import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import React, { useState } from "react";
import type { Resolver } from "react-hook-form";
import { useForm } from "react-hook-form";

import { toast } from "@/components/ui/use-toast";
import { getPanelBrandSlug } from "@/config/config";
import { useConfirmDialog } from "@/context";
import { useI18n } from "@/context/i18n";
import { queryClient } from "@/lib/api";
import { getPaymentGateways } from "@/pages/payment-gateways/payment-gateways.api";

import * as productsApi from "./products.api";
import { isInfinityAssistanceProduct } from "./products.assistance-variant";
import { IUseProduct } from "./products.hooks.interface";
import {
  assistanceSchema,
  autoSchema,
  baseProductSchema,
  getDefaultSoulhubAssistanceInsuranceForm,
  groupLifeSchema,
  personalAccidentSchema,
  soulhubAssistanceInsuranceSchema,
  TProductFormData,
} from "./products.schema";
import { EProductType, TProduct } from "./products.type";
import { normalizeVehicleCommercialFromApi } from "./schemas";
import {
  creditProductFormSchema,
  getDefaultCreditProductForm,
  mergeCreditProductConfigFromApi,
} from "./schemas/credit-product.schema";
import {
  getDefaultTheftProductForm,
  theftInsuranceSchema,
} from "./schemas/theft-insurance.schema";
import {
  getDefaultWarrantyProductForm,
  mergeAssistanceProductConfigFromApi,
  warrantyInsuranceSchema,
} from "./schemas/warranty-insurance.schema";

export const useProduct = (): IUseProduct => {
  const { t } = useI18n();
  const { openDialog } = useConfirmDialog();
  const [isProductFormOpen, setIsProductFormOpen] =
    React.useState<boolean>(false);
  const openProductForm = React.useCallback(() => {
    setIsProductFormOpen(true);
  }, []);
  const [productType, setProductType] = useState<EProductType | null>(null);
  const [editingProductId, setEditingProductId] = React.useState<string | null>(
    null
  );
  const { data: products, error: productsError } = useQuery({
    queryKey: ["products"],
    queryFn: productsApi.getProducts,
  });

  const { data: paymentGatewaysList } = useQuery({
    queryKey: ["payment-gateways"],
    queryFn: () => getPaymentGateways(false),
  });
  const editingProduct = React.useMemo(() => {
    if (!editingProductId) return null;
    return (products || []).find((p) => p.id === editingProductId) ?? null;
  }, [editingProductId, products]);

  const getSchemaForProductType = (type: EProductType | null | undefined) => {
    switch (type) {
      case EProductType.INSURANCE_AP:
        return personalAccidentSchema;
      case EProductType.INSURANCE_VG:
        return groupLifeSchema;
      case EProductType.INSURANCE_ASSISTANCE:
        if (isInfinityAssistanceProduct(editingProduct)) {
          return assistanceSchema;
        }
        return getPanelBrandSlug() === "soulhub"
          ? soulhubAssistanceInsuranceSchema
          : assistanceSchema;
      case EProductType.AUTO:
      case EProductType.MOTO:
        return autoSchema;
      case EProductType.CREDIT:
        return creditProductFormSchema;
      case EProductType.WARRANTY_INSURANCE:
        return warrantyInsuranceSchema;
      case EProductType.THEFT_INSURANCE:
        return theftInsuranceSchema;
      default:
        return baseProductSchema;
    }
  };

  const productForm = useForm<TProductFormData>({
    resolver: (async (values, context, options) => {
      const typeToUse =
        productType ||
        ((values as { type?: EProductType }).type as EProductType | undefined);
      const schema = getSchemaForProductType(typeToUse);
      return (zodResolver(schema) as any)(values, context, options);
    }) as Resolver<TProductFormData>,
    defaultValues: {
      type: EProductType.INSURANCE_AP,
      name: "",
      insurance_company_id: "",
      payment_gateway_id: "",
      policy_number: "",
      cod_sub: "",
      susep_code: "",
      iof_value: 0,
      type_of_vigency: "annual",
      policy_start_date: undefined,
      policy_end_date: undefined,
      min_permitted_age: undefined,
      max_permitted_age: undefined,
      description: "",
      insurance_coverages: "",
      insurance_benefits: "",
      general_conditions: "",
      external_product_id: "",
      capital_ranges: [
        {
          capital_value: undefined,
          premium_value: 0,
          net_premium: undefined,
          description: "",
          markup: 0,
          markup_mode: "percentage",
          plan_name: "",
        },
      ],
      capital_value: undefined,
      commission_distribution: [
        {
          agency: 0,
          commission: 0,
          pro_labore: 0,
          markup: undefined,
          markup_mode: "percentage",
        },
      ],
      commission_lifetime_other_installments: 0,
      pro_labore_lifetime_other_installments: 0,
      commission_unique: 0,
      pro_labore_unique: 0,
      policy_holder: "",
      secondary_policy_holder: "",
      table_code: "",
      insurance_policy_extra_price_code: "",
      stipulant_code: "",
      sub_stipulant_code: "",
      code_plan: "",
      plans: [],
    },
  });

  // React.useEffect(() => {
  //   const subscription = productForm.watch((value, { name }) => {
  //     if (name === "type_of_vigency") {
  //       const commission =
  //         value.type_of_vigency === "lifetime" ? "lifetime" : "unique";
  //       productForm.setValue("type_of_vigency", commission);
  //     }
  //   });
  //   return () => subscription.unsubscribe();
  // }, [productForm]);

  React.useEffect(() => {
    // Em edição (lista), beginEditProduct já fez reset com os dados do produto;
    // não sobrescrever com defaults vazios.
    if (editingProductId) {
      return;
    }

    // Para Auto, não definir datas padrão e tornar alguns campos opcionais
    const isAuto = productType === EProductType.AUTO;
    const isMoto = productType === EProductType.MOTO;
    const isVehicle = isAuto || isMoto;
    const isAssistance = productType === EProductType.INSURANCE_ASSISTANCE;
    const today = new Date();
    
    const baseReset = {
      type: productType || EProductType.INSURANCE_AP,
      name: "",
      insurance_company_id: "",
      policy_number: isVehicle ? null : (isAssistance ? "N/A" : ""),
      cod_sub: isVehicle ? null : "",
      susep_code: isVehicle ? null : (isAssistance ? "N/A" : ""),
      iof_value: 0,
      type_of_vigency: "annual",
      policy_start_date: isVehicle ? today : undefined,
      policy_end_date: isVehicle ? today : undefined,
      description: "",
      insurance_coverages: "",
      insurance_benefits: "",
      general_conditions: "",
      external_product_id: "",
      payment_gateway_id: "",
      capital_ranges: [
        {
          capital_value: undefined,
          premium_value: 0,
          net_premium: undefined,
          description: "",
          markup: 0,
          markup_mode: "percentage",
          plan_name: "",
        },
      ],
      capital_value: undefined,
      commission_distribution: [
        {
          agency: 0,
          commission: 0,
          pro_labore: 0,
          markup: undefined,
          markup_mode: "percentage",
        },
      ],
      commission_lifetime_other_installments: 0,
      pro_labore_lifetime_other_installments: 0,
      commission_unique: 0,
      pro_labore_unique: 0,
      policy_holder: "",
      secondary_policy_holder: "",
      table_code: "",
      insurance_policy_extra_price_code: "",
      stipulant_code: "",
      sub_stipulant_code: "",
    };

    if (productType === EProductType.CREDIT) {
      productForm.reset(getDefaultCreditProductForm() as any);
    } else if (productType === EProductType.WARRANTY_INSURANCE) {
      productForm.reset(getDefaultWarrantyProductForm() as any);
    } else if (productType === EProductType.THEFT_INSURANCE) {
      productForm.reset(getDefaultTheftProductForm() as any);
    } else if (productType === EProductType.AUTO || productType === EProductType.MOTO) {
      productForm.reset({
        ...baseReset,
        type: productType,
        km_max_permitted: undefined,
        min_manufacturing_year: undefined,
        plans: [],
        vehicle_emission_type: "bilhete",
        is_collective_policy: false,
        has_estipulante: false,
        has_sub_estipulante: false,
        susep_product_code: "NA",
        has_km_limit: true,
        has_manufacturing_year_limit: true,
        max_vehicle_age: undefined,
        excluded_risks: "",
        premium_mode: "calculated",
        prefixed_premium_value: undefined,
        has_multiple_plans: true,
        vehicle_product_config: {
          commercial: {
            commission_enabled: true,
            commission_mode: "percentage",
            commission_value: 0,
            pro_labore_enabled: false,
            pro_labore_mode: "real_currency",
            pro_labore_value: 0,
            markup_enabled: false,
            markup_mode: "percentage",
            markup_value: 0,
          },
        },
      } as any);
    } else if (productType === EProductType.INSURANCE_ASSISTANCE) {
      if (getPanelBrandSlug() === "soulhub") {
        productForm.reset(
          getDefaultSoulhubAssistanceInsuranceForm() as any,
        );
      } else {
        productForm.reset({
          ...baseReset,
          min_permitted_age: 1,
          max_permitted_age: 1,
        } as any);
      }
    } else {
      productForm.reset({
        ...baseReset,
        min_permitted_age: undefined,
        max_permitted_age: undefined,
      } as any);
    }
  }, [productForm, productType, editingProductId]);

  React.useEffect(() => {
    if (editingProductId || !productType || !paymentGatewaysList?.length) {
      return;
    }
    const soulhubGw = paymentGatewaysList.find((g) => g.slug === "soulhub");
    const current = (productForm.getValues() as { payment_gateway_id?: string })
      .payment_gateway_id;
    if (soulhubGw?.id && (!current || current === "")) {
      productForm.setValue(
        "payment_gateway_id" as const,
        soulhubGw.id,
      );
    }
  }, [
    editingProductId,
    productType,
    paymentGatewaysList,
    productForm,
  ]);

  const beginEditProduct = React.useCallback(
    (productId: string) => {
      const product = (products || []).find((p) => p.id === productId);
      if (!product) return;

      setEditingProductId(productId);
      setProductType(product.type as EProductType);
      setIsProductFormOpen(true);

      if (product.type === EProductType.CREDIT) {
        const p = product as TProduct;
        productForm.reset({
          type: EProductType.CREDIT,
          name: p.name || "",
          insurance_company_id: p.insurance_company_id || "",
          payment_gateway_id: p.payment_gateway_id || "",
          infinity_sync_as_source: p.infinity_sync_as_source ?? true,
          credit_product_config: mergeCreditProductConfigFromApi(
            p.credit_product_config,
          ),
        } as any);
        return;
      }

      if (product.type === EProductType.WARRANTY_INSURANCE) {
        productForm.reset({
          ...(product as Record<string, unknown>),
          type: EProductType.WARRANTY_INSURANCE,
        } as any);
        return;
      }

      if (product.type === EProductType.THEFT_INSURANCE) {
        productForm.reset({
          ...(product as Record<string, unknown>),
          type: EProductType.THEFT_INSURANCE,
        } as any);
        return;
      }

      if (
        product.type === EProductType.INSURANCE_ASSISTANCE &&
        getPanelBrandSlug() === "soulhub" &&
        !isInfinityAssistanceProduct(product)
      ) {
        const p = product as Record<string, unknown>;
        productForm.reset({
          ...p,
          type: EProductType.INSURANCE_ASSISTANCE,
          assistance_product_config: mergeAssistanceProductConfigFromApi(
            (p as { assistance_product_config?: unknown })
              .assistance_product_config,
          ),
          excluded_risks:
            (p as { excluded_risks?: string }).excluded_risks ?? "",
          policy_start_date: product.policy_start_date
            ? new Date(product.policy_start_date)
            : new Date(),
          policy_end_date: product.policy_end_date
            ? new Date(product.policy_end_date)
            : new Date(),
        } as any);
        productForm.clearErrors();
        return;
      }

      // Os dados já vêm convertidos da API via mapper
      const isAuto = product.type === EProductType.AUTO;
      const isMoto = product.type === EProductType.MOTO;
      const isVehicleProduct = isAuto || isMoto;
      
      // Os dados já vêm convertidos da API via mapper (autoToFrontend converte capital_ranges em plans)
      productForm.reset({
        ...product,
        policy_number:
          (product as any).policy_number ||
          (product as any).apolice_number ||
          (isVehicleProduct ? null : ""),
        cod_sub:
          (product as any).cod_sub ||
          (product as any).apolice_number_secondary ||
          (isVehicleProduct ? null : ""),
        susep_code: isVehicleProduct
          ? ((product as any).susep_code || null)
          : ((product as any).susep_code || ""),
        policy_start_date: product.policy_start_date
          ? new Date(product.policy_start_date)
          : (isVehicleProduct ? null : new Date()),
        policy_end_date: product.policy_end_date
          ? new Date(product.policy_end_date)
          : (isVehicleProduct ? null : new Date()),
        commission_distribution:
          ((product as any).commission_distribution && (product as any).commission_distribution.length > 0)
            ? (product as any).commission_distribution
            : [{ agency: 0, commission: 0, pro_labore: 0, markup: 0, markup_mode: "percentage" }],
        // Preservar plans para produtos Auto (já vem convertido do mapper)
        plans: (product as any).plans || [],
        // Campos específicos do Auto
        km_max_permitted: (product as any).km_max_permitted,
        min_manufacturing_year: (product as any).min_manufacturing_year,
        vehicle_emission_type: (product as any).vehicle_emission_type ?? "apolice",
        is_collective_policy: (product as any).is_collective_policy ?? false,
        has_estipulante: (product as any).has_estipulante ?? false,
        has_sub_estipulante: (product as any).has_sub_estipulante ?? false,
        susep_product_code: (product as any).susep_product_code ?? "NA",
        has_km_limit: (product as any).has_km_limit ?? true,
        has_manufacturing_year_limit:
          (product as any).has_manufacturing_year_limit ?? true,
        max_vehicle_age: (product as any).max_vehicle_age,
        excluded_risks: (product as any).excluded_risks ?? "",
        premium_mode: (product as any).premium_mode ?? "calculated",
        prefixed_premium_value: (product as any).prefixed_premium_value,
        has_multiple_plans: (product as any).has_multiple_plans ?? true,
        vehicle_product_config: (() => {
          const raw = (product as any).vehicle_product_config;
          return {
            commercial: normalizeVehicleCommercialFromApi(raw?.commercial),
          };
        })(),
      } as any);
    },
    [productForm, products]
  );

  const createProductMutation = useMutation({
    mutationFn: productsApi.createProduct,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast({
        title: t("generic.success"),
        description: t("products.success.productCreated"),
      });
      setIsProductFormOpen(false);
      setProductType(null);
      productForm.reset();
    },
    onError: (error: Error) => {
      console.error("=== CREATE PRODUCT ERROR ===");
      console.error("Error:", error);
      console.error("Message:", error.message);
      if (error.stack) {
        console.error("Stack:", error.stack);
      }
      
      toast({
        title: t("generic.error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });


  const createProduct = React.useCallback(
    (data: TProductFormData) => {
      // Garantir que o tipo seja definido
      const type = productType || data.type || EProductType.INSURANCE_AP;
      const payload = { ...data, type };
      
      createProductMutation.mutate(payload as any);
    },
    [createProductMutation, productType]
  );

  const updateProductMutation = useMutation({
    mutationFn: ({
      productId,
      data,
    }: {
      productId: string;
      data: TProductFormData;
    }) => productsApi.updateProduct(productId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast({
        title: t("generic.success"),
        description: t("products.success.productUpdated"),
      });
      setIsProductFormOpen(false);
      setProductType(null);
      productForm.reset();
      setEditingProductId(null);
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateProduct = React.useCallback(
    (productId: string, data: TProductFormData) => {
      // Garantir que o tipo seja definido
      const type = productType || data.type || EProductType.INSURANCE_AP;
      const payload = { ...data, type };

      updateProductMutation.mutate({
        productId,
        data: payload as any,
      });
    },
    [updateProductMutation, productType]
  );

  const deleteProductMutation = useMutation({
    mutationFn: productsApi.deleteProduct,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["products"] });
      await queryClient.refetchQueries({ queryKey: ["products"] });

      toast({
        title: t("generic.success"),
        description: t("products.success.productDeleted"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteProduct = React.useCallback(
    (productId: string) => {
      const product = products?.find((p) => p.id === productId);
      openDialog(t("generic.confirmDelete"), product?.name || "", () =>
        deleteProductMutation.mutate(productId)
      );
    },
    [deleteProductMutation]
  );

  React.useEffect(() => {
    if (productsError) {
      toast({
        title: t("generic.error"),
        description: productsError.message,
        variant: "destructive",
      });
    }
  }, [productsError, t]);

  const productsList = React.useMemo(
    () => (products != null && Array.isArray(products) ? products : []),
    [products],
  );

  return {
    isProductFormOpen,
    setIsProductFormOpen,
    openProductForm,
    productForm,
    createProduct,
    updateProduct,
    deleteProduct,
    products: productsList,
    productType,
    setProductType,
    beginEditProduct,
    editingProductId,
    setEditingProductId,
    editingProduct,
    isEditing: !!editingProductId,
  };
};
